import { useCallback, useEffect, useMemo, useRef } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ArrowForward, AtOutline, TeachMode } from '@lyearn/icons';
import { Editor } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import { Button, DeprecatedIconButton, LinkButton, Stack, Typography } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';

import useCoachInput from '../../hooks/useCoachInput';
import {
  iconButtonStyles,
  lightPurpleBg,
  maxWidth768,
  placeholderColor,
  purpleBorder,
  shakeAnimation,
} from '../../styles';
import AiWarning from '../AiWarning';
import { mentionHelperStyles } from '../ChatView/styles';
import ToggleTeachModeModal from './modal';

interface ChatInputProps {
  message: string;
  isWaiting: boolean;
  onMessageChange: (value: string) => void;
  onEnter: (text: string) => void;
  onClickSend: (firstMessage: boolean) => void;
  error: boolean;
  teachModeEnabled: boolean;
  toggleTeachMode: () => void;
  clearChannel: () => void;
}

export const ChatInput = ({
  message,
  isWaiting,
  onMessageChange,
  onEnter,
  onClickSend,
  error,
  teachModeEnabled,
  toggleTeachMode,
  clearChannel,
}: ChatInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isTeachModeModalOpen, { on, off }] = useToggle(false);
  const { [FeatureFlags.EnableKnowledgeBaseTeachMode]: isTeachModeFeatureEnabled } =
    useFeatureFlags();

  useEffect(() => {
    if (inputRef.current) {
      const inputElement = inputRef.current.querySelector('textarea');
      inputElement?.focus();
    }
  }, [inputRef.current]);

  const handleInputChange = useCallback(
    (editorMessage: string, textContent: string) => {
      onMessageChange(editorMessage);
    },
    [onMessageChange],
  );

  const placeholder = useMemo(() => t('Common:KnowledgeBase.KnowledgeBaseInputPlaceholder'), [t]);

  const { editorProps, mentionsButtonProps } = useCoachInput({
    onInputChange: handleInputChange,
    placeholder,
    onEnter,
  });

  const onConfirm = useCallback(() => {
    toggleTeachMode();
    off();
    clearChannel();
  }, [toggleTeachMode, off, clearChannel]);

  return (
    <Stack
      className={cn(
        maxWidth768,
        shakeAnimation,
        'w-full mx-auto pb-12 px-16 sticky bottom-0 bg-bg-primary',
      )}
      direction="col"
      gap={12}>
      <Stack
        className={cn(
          'p-12 rounded-16 border shadow-02 text-14',
          placeholderColor,
          mentionHelperStyles,
          teachModeEnabled ? purpleBorder : 'border-border',
          teachModeEnabled ? lightPurpleBg : 'bg-bg-primary',
        )}
        direction="col"
        gapY={24}>
        <Editor {...editorProps} />
        <Stack
          alignItems="center"
          direction="row"
          justifyContent={isTeachModeFeatureEnabled ? 'between' : 'end'}>
          {isTeachModeFeatureEnabled ? (
            <Button
              appearance="secondary"
              className={cn(teachModeEnabled ? 'bg-bg-purple-light' : '')}
              size="x-small"
              startIcon={
                <TeachMode
                  className="max-h-[16px] max-w-[16px]"
                  fillColorToken="transparent"
                  height={16}
                  width={16}
                />
              }
              variant="outlined"
              onClick={on}>
              <Typography variant="body-short-02">{t('Channel:TeachMode')}</Typography>
            </Button>
          ) : null}
          <Stack gap={8}>
            <LinkButton
              className={cn(iconButtonStyles, 'py-2 px-4')}
              color={'internal'}
              onClick={mentionsButtonProps.onMentionButtonClick}>
              <AtOutline className="fill-current" />
            </LinkButton>
            <DeprecatedIconButton
              appearance={!message.length ? 'secondary' : 'primary'}
              className={'mt-auto'}
              disabled={!message.length}
              size="x-small"
              variant={!message.length ? 'ghost' : 'filled'}
              onClick={() => onClickSend(false)}>
              <ArrowForward className="fill-current -rotate-90" />
            </DeprecatedIconButton>
          </Stack>
          {isTeachModeModalOpen ? (
            <ToggleTeachModeModal
              isTeachModeEnabled={teachModeEnabled}
              onClose={off}
              onConfirm={onConfirm}
            />
          ) : null}
        </Stack>
      </Stack>
      <AiWarning />
    </Stack>
  );
};
